import { FC, useState, useRef, useMemo, useEffect } from 'react';
import 'handsontable/dist/handsontable.full.css';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { successToast, errorToast } from '@/helpers';
import { ApiService } from '@/support/ApiService.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { _notNil, _isEmptyString, _isNil } from '@/littledash';
import {
  validateSampleTypeValue,
  validateSampleTypeLabel,
  getBasicSampleTypeErrorMessage,
  getBasicLabelErrorMessage,
  ValidationData,
} from '@/utils/sampleTypeValidation';
import type { SampleType, SampleTypeRow } from '@/model/SampleType.model';
import type { components } from '@/generated/internal-api/openapi-types';
import { registerHOTModules } from '@/support/hot';
import SpreadSheet from '@/components/UI/SpreadSheet';
import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';

registerHOTModules();
MultiSelectEditor.register();

type SampleUpdatePayloadItem = components['schemas']['TeamGlossarySamplesUpdateItemV1.schema'];

interface BulkEditSampleTypesProps {
  sampleTypes: Array<SampleType>;
  onSave: () => void;
  onCancel: () => void;
}

const BulkEditSampleTypes: FC<BulkEditSampleTypesProps> = ({ sampleTypes, onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [loadingValidation, setLoadingValidation] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const hotTableRef = useRef<any>(null);

  const availableDetailOptions = useMemo(() => {
    return ['Weight (mg g µg)', 'Volume (ml µl cm³ mm³)', 'Width (mm)', 'Length (mm)', 'Depth (mm)'];
  }, []);

  useEffect(() => {
    const fetchValidationData = async (): Promise<void> => {
      try {
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/team-glossary/sample-validation-data',
          path: undefined,
          options: {
            onError: { toast: false, throw: true, capture: false, slug: 'bulk-edit-sample-types-validation-load' },
          },
        });
        setValidationData(response.body as ValidationData);
      } catch (error) {
        errorToast('Failed to load validation data');
        ExceptionHandler.captureException(
          new InVivoError('Failed to load validation data', {
            cause: error,
            slug: 'bulk-edit-sample-types-validation-load',
          })
        );
        setValidationData(null);
      } finally {
        setLoadingValidation(false);
      }
    };

    void fetchValidationData();
  }, []);

  // Validation helper functions
  const validateTitle = (value: string, rowIndex: number): { isValid: boolean; message?: string } => {
    const hotInstance = hotTableRef.current?.hotInstance;
    const spreadsheetData = hotInstance ? (hotInstance.getData() as Array<SampleTypeRow>) : [];

    return validateSampleTypeValue(value, rowIndex, validationData, sampleTypes, spreadsheetData);
  };

  const validateLabel = (value: string, rowIndex: number): { isValid: boolean; message?: string } => {
    const hotInstance = hotTableRef.current?.hotInstance;
    const spreadsheetData = hotInstance ? (hotInstance.getData() as Array<SampleTypeRow>) : [];

    return validateSampleTypeLabel(value, rowIndex, validationData, sampleTypes, spreadsheetData);
  };

  const displayToApiMap: Record<string, string> = useMemo(
    () => ({
      'Weight (mg g µg)': 'weight',
      'Volume (ml µl cm³ mm³)': 'volume',
      'Width (mm)': 'width',
      'Length (mm)': 'length',
      'Depth (mm)': 'depth',
    }),
    []
  );

  const apiToDisplayMap: Record<string, string> = useMemo(
    () => ({
      weight: 'Weight (mg g µg)',
      volume: 'Volume (ml µl cm³ mm³)',
      width: 'Width (mm)',
      length: 'Length (mm)',
      depth: 'Depth (mm)',
    }),
    []
  );

  const initialData: Array<SampleTypeRow> = useMemo(() => {
    return sampleTypes.map((sampleType) => {
      const detailsApiValues = sampleType.options?.details || [];

      const detailsDisplayLabels = detailsApiValues.map((apiValue) => apiToDisplayMap[apiValue] || apiValue);

      const joinedDetails = detailsDisplayLabels.join(', ');

      return [sampleType.title, sampleType.label || '', joinedDetails];
    });
  }, [sampleTypes, availableDetailOptions]);

  const columns = useMemo(
    () => [
      {
        data: 0,
        title: 'Sample type',
        type: 'text',
        className: 'htMiddle',
        width: 140,
        validator: function (this: any, value: unknown, callback: (valid: boolean) => void) {
          if (_isNil(value) || typeof value !== 'string') {
            callback(false);
            return;
          }

          const rowIndex = this.row;
          if (typeof rowIndex !== 'number') {
            callback(true);
            return;
          }

          const validation = validateTitle(value, rowIndex);
          callback(validation.isValid);
        },
        validationErrorMessage: (value: unknown) => {
          return getBasicSampleTypeErrorMessage(value as string, validationData);
        },
      },
      {
        data: 1,
        title: 'Label',
        type: 'text',
        className: 'htMiddle',
        width: 120,
        validator: function (this: any, value: unknown, callback: (valid: boolean) => void) {
          if (_isNil(value)) {
            callback(true);
            return;
          }

          if (typeof value !== 'string') {
            callback(false);
            return;
          }

          const rowIndex = this.row;
          if (typeof rowIndex !== 'number') {
            callback(true);
            return;
          }

          const validation = validateLabel(value, rowIndex);
          callback(validation.isValid);
        },
        validationErrorMessage: (value: unknown) => {
          return getBasicLabelErrorMessage(value as string, validationData);
        },
      },
      {
        data: 2,
        title: 'Details',
        type: 'multi-select',
        className: 'htMiddle',
        width: 280,
        options: availableDetailOptions,
        isMultiSelect: true,
        jsonEncoded: false,
        validator: (value: unknown, callback: (valid: boolean) => void) => {
          if (_isNil(value) || value === '') {
            callback(true);
            return;
          }

          const selectedOptions =
            typeof value === 'string'
              ? value
                  .split(',')
                  .map((v) => v.trim())
                  .filter(Boolean)
              : [];

          const allValid = selectedOptions.every((option) => availableDetailOptions.includes(option));
          callback(allValid);
        },
      },
    ],
    [availableDetailOptions, validationData, sampleTypes]
  );

  const hotTableSettings = useMemo(
    () => ({
      data: initialData,
      colHeaders: ['Sample type', 'Label', 'Details'],
      columns,
      rowHeaders: true,
      manualColumnResize: true,
      contextMenu: false,
      fillHandle: false,
      outsideClickDeselects: false,
      width: '100%',
      height: 600,
      rowHeights: 50,
      className: 'htMiddle ',
      licenseKey: AppConfig.handsOnTableLicence,
      stretchH: 'all' as 'none' | 'all' | 'last' | undefined,
      afterRenderer: function (
        TD: HTMLElement,
        row: number,
        col: number,
        prop: string | number,
        value: any,
        cellProperties: any
      ) {
        if (cellProperties.readOnly) {
          const sampleType = sampleTypes[row];
          if (_isNil(sampleType)) {
            return;
          }

          const isSystem = sampleType.system === true;
          const isArchived = _notNil(sampleType.archived_at);

          if (isSystem) {
            TD.setAttribute('data-tooltip-id', 'global-tooltip-id');
            TD.setAttribute('data-tooltip-content', 'This is a system sample type and cannot be modified');
            TD.setAttribute('data-type', 'info');
          } else if (isArchived) {
            TD.setAttribute('data-tooltip-id', 'global-tooltip-id');
            TD.setAttribute('data-tooltip-content', 'This sample type is archived and cannot be modified');
            TD.setAttribute('data-type', 'info');
          } else {
            TD.setAttribute('data-tooltip-id', 'global-tooltip-id');
            TD.setAttribute('data-tooltip-content', 'This sample type is in use and cannot be modified');
            TD.setAttribute('data-type', 'info');
          }
        }
      },
      cells: function (row: number, col: number) {
        const cellProperties: any = {};

        if (_notNil(sampleTypes[row])) {
          const isSystem = sampleTypes[row].system === true;
          const isArchived = _notNil(sampleTypes[row].archived_at);

          // System and archived sample types are fully read-only (all columns)
          if (isSystem || isArchived) {
            cellProperties.readOnly = true;
            cellProperties.className = 'htMiddle readOnly';
          } else if (_notNil(validationData)) {
            // In-use sample types only have the title column read-only
            const isInUse = validationData.in_use_team_glossary_api_ids.includes(sampleTypes[row].api_id);
            if (col === 0 && isInUse) {
              cellProperties.readOnly = true;
              cellProperties.className = 'htMiddle readOnly';
            }
          }
        }

        return cellProperties;
      },
    }),
    [initialData, columns, sampleTypes, validationData]
  );

  const handleSave = async () => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    setSaving(true);

    try {
      const currentData = (hotInstance.getData?.() ?? []) as Array<SampleTypeRow>;

      const teamGlossariesPayload = currentData.reduce<Array<SampleUpdatePayloadItem>>((acc, row, index) => {
        const [title, label, detailsRaw] = row;
        const sampleType = sampleTypes[index];

        // Skip system and archived sample types - they are read-only
        const isSystem = sampleType.system === true;
        const isArchived = _notNil(sampleType.archived_at);
        if (isSystem || isArchived) {
          return acc;
        }

        const detailsDisplay = Array.isArray(detailsRaw)
          ? detailsRaw.map((d) => String(d).trim()).filter(Boolean)
          : detailsRaw
            ? String(detailsRaw)
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean)
            : [];
        const detailsApi = detailsDisplay
          .map((d) => displayToApiMap[d])
          .filter(
            (d): d is 'volume' | 'weight' | 'width' | 'length' | 'depth' =>
              d !== undefined && ['volume', 'weight', 'width', 'length', 'depth'].includes(d)
          );
        const maybeTitle = (title ?? '').toString().trim();
        const maybeLabel = (label ?? '').toString().trim();

        acc.push({
          team_glossary_api_id: sampleType.api_id,
          title: maybeTitle,
          options: { details: detailsApi },
          ...(_notNil(maybeLabel) && !_isEmptyString(maybeLabel) && { label: maybeLabel }),
        });
        return acc;
      }, []);

      await ApiService.call({
        endpoint: 'PATCH /api/v1/team-glossary',
        path: undefined,
        body: { team_glossaries: teamGlossariesPayload },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-edit-sample-types-save' } },
      });

      successToast('Sample types updated successfully');

      onSave();
    } catch (error) {
      errorToast('Failed to update sample types');
      ExceptionHandler.captureException(
        new InVivoError('Failed to update sample types', {
          cause: error,
          slug: 'bulk-edit-sample-types-save',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalContainer className="bg-white">
      <ModalHeader
        title="Edit sample types"
        closeModal={onCancel}
        closeDisabled={saving}
        className="pa3 bb b--moon-gray w-100"
      />

      <div className="pa3">
        {loadingValidation ? (
          <div className="tc pa4">
            <div className="mb2">Loading validation data...</div>
          </div>
        ) : (
          <div className="mv3 w-100 overflow-auto ow-spreadsheet-styles">
            <SpreadSheet
              className={'mt3'}
              data={initialData}
              settings={hotTableSettings}
              addCol={false}
              innerRef={hotTableRef}
              maxRows={initialData.length}
              setInvalid={setHasValidationErrors}
            />
          </div>
        )}
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={onCancel}
        submitBtnText="Update"
        cancelBtnText="Close"
        submitButtonProps={{
          loading: saving,
          disabled: saving || loadingValidation || hasValidationErrors,
        }}
        cancelButtonProps={{
          disabled: saving,
        }}
      />
    </ModalContainer>
  );
};

export default BulkEditSampleTypes;
